import { type ReactNode, useCallback, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useSubject } from '@/hooks/rx-hooks'
import { animationFrameScheduler, BehaviorSubject } from 'rxjs'
import {
  useIsWizardSidePanelOffside,
  WizardSidePanel,
  WizardSidePanelFloatingCloseButton,
  type WizardSidePanelProps,
  WizardSidePanelScroller,
} from '@/components/Wizard/WizardSidePanel'
import { AnimatePresence } from 'motion/react'
import { observeOn } from 'rxjs/operators'
import { ConversationDialog } from '@/features/messages/components/ConversationDialog'
import { Provider } from 'react-aria-components'
import { ConversationLayoutContext } from '@/features/messages/components/ConversationLayout'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { ChatIconButton } from '@/components/ChatIconButton'
import type { ConversationViewModel } from '@/features/messages/ConversationViewModel'

export const CHAT_BUTTON_LAYOUT_ID = 'info-gathering-chat-button'

interface InfoGatheringWizardChatIconButtonContainerProps {
  isChatAvailable: boolean
  isChatOpen: boolean
  conversationVm: ConversationViewModel
  onOpenPress: () => unknown
  defaultAvatarUrl?: string
  testId?: string
}

/**
 * Chat Icon Button Container
 */
export const InfoGatheringWizardChatIconButtonContainer = observer(
  function InfoGatheringWizardChatIconButtonContainer({
    isChatAvailable,
    isChatOpen,
    onOpenPress,
    conversationVm,
    defaultAvatarUrl,
    testId,
  }: InfoGatheringWizardChatIconButtonContainerProps) {
    const isSpacious = useIsSpacious()

    const avatarUrl = getAvatarUrl(conversationVm) ?? defaultAvatarUrl

    if (!isSpacious) {
      return (
        <ChatIconButton
          label="Open chat"
          avatarUrl={avatarUrl}
          onPress={onOpenPress}
          badgeCount={conversationVm.unreadCount}
          testId={testId}
        />
      )
    }

    return (
      <AnimatePresence mode="popLayout" initial={false}>
        {isChatAvailable && !isChatOpen && (
          <ChatIconButton
            label="Open chat"
            avatarUrl={avatarUrl}
            layout={isSpacious}
            layoutId={CHAT_BUTTON_LAYOUT_ID}
            onPress={onOpenPress}
            badgeCount={conversationVm.unreadCount}
            testId={testId}
          />
        )}
      </AnimatePresence>
    )
  },
)

/**
 * Gets the avatar URL to use for the chat button based on the last message
 * that was sent which was not the current user.
 *
 * @param conversationVm
 */
function getAvatarUrl(
  conversationVm: ConversationViewModel,
): string | undefined {
  for (let i = conversationVm.messages.length - 1; i >= 0; i--) {
    const vm = conversationVm.messages[i]
    if (vm.message.sender.isCurrentUser) {
      continue
    }

    if (vm.message.sender.isSystemRobot) {
      continue
    }

    if (vm.message.sender.avatarUrl) {
      return vm.message.sender.avatarUrl
    }
  }

  return undefined
}

interface InfoGatheringWizardConversationSidePanelContainerProps {
  children?: ReactNode
  isOpen: boolean
  positioning?: WizardSidePanelProps['positioning']
  onOpenChange: (isOpen: boolean) => unknown
}

/**
 * Conversation Side Panel Container
 */
export const InfoGatheringWizardConversationSidePanelContainer = observer(
  function InfoGatheringWizardConversationSidePanelContainer({
    children,
    isOpen,
    onOpenChange,
    positioning,
  }: InfoGatheringWizardConversationSidePanelContainerProps) {
    const [scrollingElement, connectScrollRef] =
      useState<HTMLDivElement | null>()

    const close = useCallback(() => onOpenChange(false), [onOpenChange])
    const isReadyToScroll$ = useSubject(() => new BehaviorSubject(isOpen))
    if (scrollingElement) {
      // Become ready to scroll once we have the scrolling element.
      if (isReadyToScroll$.value !== isOpen) {
        isReadyToScroll$.next(isOpen)
      }
    }

    return (
      <WizardSidePanel
        justify="end"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        transitionDelay={isOpen ? 0 : 0.175}
        aria-label="Conversation"
        backgroundColor="secondary"
        positioning={positioning}
      >
        <AnimatePresence initial={false} mode="popLayout">
          {isOpen && positioning !== 'offside' && (
            <WizardSidePanelFloatingCloseButton
              layout
              layoutId={CHAT_BUTTON_LAYOUT_ID}
              onPress={close}
              transitionDelay={isOpen ? 0.175 : 0}
            />
          )}
        </AnimatePresence>
        <WizardSidePanelScroller ref={connectScrollRef}>
          <Provider
            values={[
              [
                ConversationLayoutContext,
                {
                  composerPositioning: 'sticky',
                  spacing: 'compact',
                  isComposerHidden: !isOpen,
                  composerMask: isOpen ? 'visible' : 'none',
                  scrollingElement: scrollingElement,
                  isReadyToScroll$: isReadyToScroll$,
                },
              ],
            ]}
          >
            {children}
          </Provider>
        </WizardSidePanelScroller>
      </WizardSidePanel>
    )
  },
)

interface InfoGatheringWizardConversationDialogContainerProps {
  isOpen: boolean
  onClose: () => unknown
  children: ReactNode
}

/**
 * Conversation Dialog Container
 */
export const InfoGatheringWizardConversationDialogContainer = observer(
  function InfoGatheringWizardConversationDialogContainer({
    children,
    isOpen,
    onClose,
  }: InfoGatheringWizardConversationDialogContainerProps) {
    const isSpacious = useIsSpacious()
    const [scrollingElement, setScrollRef] = useState<HTMLDivElement | null>()

    // Make sure we're not scrolling until the element ref has been set.
    const isReadyToScrollSubject = useSubject(() => new BehaviorSubject(false))
    const connectScrollRef = useCallback(
      (element: HTMLDivElement) => {
        setScrollRef(element)
        isReadyToScrollSubject.next(true)
        return () => {
          isReadyToScrollSubject.next(false)
          setScrollRef(null)
        }
      },
      [isReadyToScrollSubject],
    )

    const isReadyToScroll$ = useMemo(
      // Need to wait until the next paint.
      () => isReadyToScrollSubject.pipe(observeOn(animationFrameScheduler)),
      [isReadyToScrollSubject],
    )

    return (
      <ConversationDialog
        isOpen={isOpen && !isSpacious}
        onClose={onClose}
        ref={connectScrollRef}
        aria-label="Conversation"
      >
        <Provider
          values={[
            [
              ConversationLayoutContext,
              {
                isReadyToScroll$,
                composerPositioning: 'sticky',
                // Gaussian blurs in dialogs get finicky, so better turn it off.
                composerMask: 'none',
                spacing: 'compact',
                scrollingElement,
              },
            ],
          ]}
        >
          {children}
        </Provider>
      </ConversationDialog>
    )
  },
)

export interface InfoGatheringWizardConversationStateProps {
  /**
   * Whether chat is available.
   */
  isChatAvailable: boolean

  /**
   * Whether to disable offside positioning.
   */
  disableOffside?: boolean
}

/**
 * Conversation open/closed state for the info gathering screen.
 *
 * Must be used inside an observer component.
 */
export function useInfoGatheringWizardConversationState({
  isChatAvailable,
  disableOffside,
}: InfoGatheringWizardConversationStateProps) {
  const isSpacious = useIsSpacious()
  const isPanelOffside = useIsWizardSidePanelOffside() && !disableOffside
  const [isChatOpen, setIsChatOpen] = useState(false)

  const effectiveIsChatOpen =
    isChatAvailable && (isSpacious && isPanelOffside ? true : isChatOpen)

  const openChat = useCallback(() => setIsChatOpen(true), [])
  const closeChat = useCallback(() => setIsChatOpen(false), [])

  return {
    openChat,
    closeChat,
    isPanelOffside,
    isChatOpen: effectiveIsChatOpen,
    setIsChatOpen,
  }
}
